import { BaseItem } from '../common';
import {
  AllergyName,
  AllergyNameToStandardAllergyMap,
  Category,
  StandardAllergy,
} from './utils';

export enum Reaction {
  AbdominalPain = 'ABDOMINAL_PAIN',
  Anaphylaxis = 'ANAPHYLAXIS',
  Angioedema = 'ANGIOEDEMA',
  Anxiety = 'ANXIETY',
  // Arthralgia = 'ARTHRALGIA',
  ChestPain = 'CHEST_PAIN',
  Confusion = 'CONFUSION',
  Cough = 'COUGH',
  DecreasedBloodPressure = 'DECREASED_BLOOD_PRESSURE',
  DecreasedHeartRate = 'DECREASED_HEART_RATE',
  Dermatitis = 'DERMATITIS',
  Diarrhea = 'DIARRHEA',
  Dizziness = 'DIZZINESS',
  Edema = 'EDEMA',
  EyeRedness = 'EYE_REDNESS',
  EyeSwelling = 'EYE_SWELLING',
  Fever = 'FEVER',
  Flushing = 'FLUSHING',
  Hallucinations = 'HALLUCINATIONS',
  Headache = 'HEADACHE',
  Hives = 'HIVES',
  IncreasedBloodPressure = 'INCREASED_BLOOD_PRESSURE',
  IncreasedHeartRate = 'INCREASED_HEART_RATE',
  IncreasedSensitivityToLight = 'INCREASED_SENSITIVITY_TO_LIGHT',
  Insomnia = 'INSOMNIA',
  IrregularHeartRate = 'IRREGULAR_HEART_RATE',
  Itching = 'ITCHING',
  JointPain = 'JOINT_PAIN',
  MusclePain = 'MUSCLE_PAIN',
  Nausea = 'NAUSEA',
  Other = 'OTHER',
  Rash = 'RASH',
  RespiratoryDistress = 'RESPIRATORY_DISTRESS',
  RingingInEars = 'RINGING_IN_EARS',
  Seizure = 'SEIZURE',
  ShortnessOfBreath = 'SHORTNESS_OF_BREATH',
  Swelling = 'SWELLING',
  StevensJohnson = 'STEVENS_JOHNSON_SYNDROME',
  Vertigo = 'VERTIGO',
  Vomiting = 'VOMITING',
  Wheezing = 'WHEEZING',
}

export const reactionMap: Record<Reaction, string> = {
  [Reaction.AbdominalPain]: 'Abdominal pain',
  [Reaction.Anaphylaxis]: 'Anaphylaxis',
  [Reaction.Angioedema]: 'Angioedema',
  [Reaction.Anxiety]: 'Anxiety',
  // [Reaction.Arthralgia]: 'Arthralgia',
  [Reaction.ChestPain]: 'Chest pain',
  [Reaction.Confusion]: 'Confusion',
  [Reaction.Cough]: 'Cough',
  [Reaction.DecreasedBloodPressure]: 'Decreased blood pressure',
  [Reaction.DecreasedHeartRate]: 'Decreased heart rate',
  [Reaction.Dermatitis]: 'Dermatitis',
  [Reaction.Diarrhea]: 'Diarrhea',
  [Reaction.Dizziness]: 'Dizziness',
  [Reaction.Edema]: 'Edema',
  [Reaction.EyeRedness]: 'Eye redness',
  [Reaction.EyeSwelling]: 'Eye swelling',
  [Reaction.Fever]: 'Fever',
  [Reaction.Flushing]: 'Flushing',
  [Reaction.Hallucinations]: 'Hallucinations',
  [Reaction.Headache]: 'Headache',
  [Reaction.Hives]: 'Hives',
  [Reaction.IncreasedBloodPressure]: 'Increased blood pressure',
  [Reaction.IncreasedHeartRate]: 'Increased heart rate',
  [Reaction.IncreasedSensitivityToLight]: 'Increased sensitivity to light',
  [Reaction.Insomnia]: 'Insomnia',
  [Reaction.IrregularHeartRate]: 'Irregular heart rate',
  [Reaction.Itching]: 'Itching',
  [Reaction.JointPain]: 'Joint pain',
  [Reaction.MusclePain]: 'Muscle pain',
  [Reaction.Nausea]: 'Nausea',
  [Reaction.Other]: 'Other',
  [Reaction.Rash]: 'Rash',
  [Reaction.RespiratoryDistress]: 'Respiratory distress',
  [Reaction.RingingInEars]: 'Ringing in ears',
  [Reaction.Seizure]: 'Seizure',
  [Reaction.ShortnessOfBreath]: 'Shortness of breath',
  [Reaction.Swelling]: 'Swelling',
  [Reaction.StevensJohnson]: 'Stevens-Johnson',
  [Reaction.Vertigo]: 'Vertigo',
  [Reaction.Vomiting]: 'Vomiting',
  [Reaction.Wheezing]: 'Wheezing',
};

export enum Severity {
  Mild = 'MILD',
  MildToModerate = 'MILD_TO_MODERATE',
  Moderate = 'MODERATE',
  ModerateToSevere = 'MODERATE_TO_SEVERE',
  Severe = 'SEVERE',
  Fatal = 'FATAL',
}

export const severityMap: Record<Severity, string> = {
  [Severity.Mild]: 'Mild',
  [Severity.MildToModerate]: 'Mild to moderate',
  [Severity.Moderate]: 'Moderate',
  [Severity.ModerateToSevere]: 'Moderate to severe',
  [Severity.Severe]: 'Severe',
  [Severity.Fatal]: 'Fatal',
};

export enum Source {
  Ehr = 'EHR',
  Previsit = 'PREVISIT',
}

export interface CreateValues {
  patientId: string;
  name: string;
  nameRaw?: string | null;
  category?: Category | null;
  reaction?: Reaction | null;
  reactions?: Reaction[] | null;
  reactionsRaw?: Reaction[] | null;
  severity?: Severity | null;
  source: Source;
}

interface DeleteValues {
  isDeleted: boolean;
  deletedTimestamp: Date | null;
}

export type UpdateValues = Partial<CreateValues & DeleteValues>;

export interface Allergy extends BaseItem, CreateValues, DeleteValues {}

export interface PrevisitAllergy
  extends Pick<
    BaseItem & CreateValues,
    | 'id'
    | 'name'
    | 'nameRaw'
    | 'reaction'
    | 'reactions'
    | 'reactionsRaw'
    | 'category'
    | 'severity'
  > {
  canRemove: boolean;
}

export type { AllergyName, Category, StandardAllergy };
export { AllergyNameToStandardAllergyMap };
